import React, { useState, useMemo, useImperativeHandle, forwardRef } from "react";

const Table = forwardRef(({ columns, data, hiddenColumns, getCellProps }, ref) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [globalFilter, setGlobalFilter] = useState('');
  
  // Expose setGlobalFilter to parent component
  useImperativeHandle(ref, () => ({
    setGlobalFilter
  }));
  
  // Filter visible columns
  const visibleColumns = useMemo(() => {
    if (!hiddenColumns || hiddenColumns.length === 0) return columns;
    return columns.filter(col => !hiddenColumns.includes(col.accessor));
  }, [columns, hiddenColumns]);

  // Sort and filter data
  const processedData = useMemo(() => {
    let filtered = data;
    
    // Global filter
    if (globalFilter) {
      filtered = data.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(globalFilter.toLowerCase())
        )
      );
    }
    
    // Sort
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [data, globalFilter, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  return (
    <div className="table">
      <table>
        <thead>
          <tr className="tr">
            {visibleColumns.map(column => (
              <th 
                key={column.accessor} 
                className="th" 
                onClick={() => handleSort(column.accessor)}
                style={{ cursor: 'pointer' }}
              >
                {column.Header}
                <span>
                  {sortConfig.key === column.accessor && (
                    sortConfig.direction === 'asc' ? '⬇️' : '⬆️'
                  )}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {processedData.map((row, rowIndex) => (
            <tr key={rowIndex} className="tr">
              {visibleColumns.map(column => {
                const cellValue = row[column.accessor];
                const cellProps = getCellProps ? getCellProps({ 
                  column, 
                  row: { values: row, original: row },
                  value: cellValue 
                }) : {};
                
                return (
                  <td
                    key={column.accessor}
                    className="td"
                    style={column.style}
                    {...cellProps}
                  >
                    {column.Cell ? 
                      column.Cell({ value: cellValue, row: { values: row, original: row } }) : 
                      cellValue
                    }
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default Table;